import React from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Box, ButtonBase, CircularProgress, Typography } from '@mui/material';
import useBusiness from '../hooks/useBusiness';
import { BusinessThemeProvider } from '../context/BusinessThemeContext';
import BusinessLinks from '../components/BusinessLinks';
import BusinessLogo from '../components/BusinessLogo';
import { ChatSvg, InfoSvg, MicSvg } from '../components/BusinessIcons';
import { DISPLAY_FONT, FULL_SCREEN_SX, tok } from '../theme/businessTheme';
import { Business } from '../services/BusinessService';

export const BusinessNotFound: React.FC = () => (
  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, textAlign: 'center', gap: 2, color: tok('onBackground'), background: tok('background') }}>
    <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 800, fontSize: 28 }}>We couldn't find that business</Typography>
    <Typography sx={{ color: tok('onBackgroundMuted') }}>Check the link or QR code and try again.</Typography>
  </Box>
);

const Home: React.FC<{ business: Business }> = ({ business }) => {
  const navigate = useNavigate();
  const { displayName, copy, agents } = business;
  const aboutPath = `/b/${business.channelId}/about`;
  const talkAgent = agents.find((a) => a.id === business.talkAgentId && a.voiceEnabled)
    ?? agents.find((a) => a.voiceEnabled);
  const chatAgent = agents[0];
  const nameSize = displayName.length <= 14 ? 46 : displayName.length <= 24 ? 38 : 30;

  const talk = () => talkAgent && navigate(`/form/${talkAgent.id}`, { state: { autoVoice: true, fromBusiness: true } });
  const message = () => chatAgent && navigate(`/form/${chatAgent.id}`, { state: { startMode: 'typed', fromBusiness: true } });

  return (
    <Box component="main" sx={{ ...FULL_SCREEN_SX, display: 'flex', flexDirection: 'column', background: tok('background'), color: tok('onBackground') }}>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, pt: 2.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <BusinessLogo name={displayName} logoUrl={business.logoUrl} size={40} radius="12px" />
          </Box>
          <ButtonBase
            component={RouterLink}
            to={aboutPath}
            aria-label={`About ${displayName}`}
            sx={{ height: 44, px: 2, borderRadius: '22px', background: tok('secondaryAction'), color: tok('onSecondaryAction'), gap: 1, fontSize: 14, fontWeight: 600, fontFamily: 'inherit' }}
          >
            <InfoSvg size={18} /> About
          </ButtonBase>
        </Box>

        <Box sx={{ mx: 2.5, mt: 1.75, background: tok('heroCard'), color: tok('onHeroCard'), borderRadius: '30px', p: 1.5, pb: 2.5, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
          <Box sx={{ height: 150, borderRadius: '22px', background: tok('logoBackdrop'), display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            {business.logoUrl ? (
              <Box component="img" src={business.logoUrl} alt="" sx={{ maxWidth: '70%', maxHeight: '75%', objectFit: 'contain' }} />
            ) : (
              <BusinessLogo name={displayName} size={96} radius="28px" plate="transparent" />
            )}
          </Box>
          <Box sx={{ px: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {copy.eyebrow && (
              <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.12em', color: tok('onHeroCardMuted') }}>{copy.eyebrow}</Typography>
            )}
            <ButtonBase
              component={RouterLink}
              to={aboutPath}
              aria-label={`${displayName}, view info`}
              sx={{ display: 'block', textAlign: 'left', color: 'inherit', fontFamily: 'inherit', borderRadius: 1 }}
            >
              <Typography component="h1" sx={{ fontFamily: DISPLAY_FONT, fontWeight: 800, fontSize: nameSize, lineHeight: 0.96, letterSpacing: '-0.035em', wordBreak: 'break-word' }}>
                {displayName}
              </Typography>
            </ButtonBase>
            {business.summary && (
              <Typography sx={{ fontSize: 15, lineHeight: 1.4, color: tok('onHeroCard'), display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {business.summary}
              </Typography>
            )}
          </Box>
        </Box>

        <BusinessLinks links={business.links} />

        <Box component="nav" aria-label={copy.helpPrompt} sx={{ px: 2.5, pt: 2.25, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', color: tok('onBackgroundMuted'), px: 0.5, pb: 0.25, textTransform: 'uppercase' }}>
            {copy.helpPrompt}
          </Typography>
          {agents.length === 0 && (
            <Typography sx={{ color: tok('onBackgroundMuted'), px: 0.5 }}>Nothing is open for questions right now.</Typography>
          )}
          {agents.map((a) => (
            <ButtonBase
              key={a.id}
              component={RouterLink}
              to={`/form/${a.id}`}
              state={{ fromBusiness: true }}
              sx={{ minHeight: 52, borderRadius: '16px', background: tok('optionRow'), color: tok('onOptionRow'), display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, px: 2, py: 1, fontSize: 16, fontWeight: 600, fontFamily: 'inherit', textAlign: 'left', width: '100%' }}
            >
              <span>{a.title}</span>
              <span aria-hidden="true">→</span>
            </ButtonBase>
          ))}
        </Box>
      </Box>

      <Box sx={{ background: tok('dock'), borderRadius: '30px 30px 0 0', px: 2.25, pt: 2, pb: 'max(16px, env(safe-area-inset-bottom))', display: 'flex', gap: 1.25, flexShrink: 0 }}>
        {talkAgent && (
          <ButtonBase
            onClick={talk}
            sx={{ flex: 1, height: 58, borderRadius: '29px', background: tok('action'), color: tok('onAction'), gap: 1.25, fontSize: 17, fontWeight: 700, fontFamily: 'inherit' }}
          >
            <MicSvg /> {copy.talkLabel}
          </ButtonBase>
        )}
        {chatAgent && (
          <ButtonBase
            onClick={message}
            aria-label={`Message ${displayName}`}
            sx={talkAgent
              ? { width: 58, height: 58, borderRadius: '29px', background: tok('secondaryAction'), color: tok('onSecondaryAction') }
              : { flex: 1, height: 58, borderRadius: '29px', background: tok('action'), color: tok('onAction'), gap: 1.25, fontSize: 17, fontWeight: 700, fontFamily: 'inherit' }}
          >
            <ChatSvg />{!talkAgent && <span>Message us</span>}
          </ButtonBase>
        )}
      </Box>
    </Box>
  );
};

const BusinessHomeScreen: React.FC = () => {
  const { channelId } = useParams<{ channelId: string }>();
  const { business, loading } = useBusiness(channelId);
  return (
    <BusinessThemeProvider business={business}>
      {loading ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: tok('background') }}>
          <CircularProgress aria-label="Loading" sx={{ color: tok('onBackground') }} />
        </Box>
      ) : !business.loaded ? (
        <BusinessNotFound />
      ) : (
        <Home business={business} />
      )}
    </BusinessThemeProvider>
  );
};

export default BusinessHomeScreen;
